import type { IPanelAssemblyConfig, IPanelModulesConfig } from '../models/assessment-framework';

/**
 * Pure core for adding an attestation stage to an MSCAT panel assembly (Grade 9: two attestation
 * stages, modules 7 and 8).
 *
 * No Angular, no I/O, type-only imports - so it can be verified headlessly
 * (scripts/verify-mscat-attestation-stage.ts) and called from the framework settings component.
 *
 * Two facts shape every rule here:
 *  - A module id is an identity; a stage number is a position. Routing rules are keyed by module id
 *    and `startingModule` is a module id, so shifting stage numbers never touches routing.
 *  - Existing module configurations are left alone. The only field an existing module changes is
 *    `stageNumber`, and only when its stage shifts.
 *
 * Nothing here throws on bad input: `plan` reports `ok:false` with `errors`, and `apply` refuses to
 * run a plan that is not `ok`.
 */

export interface IAddAttestationStageInput {
  /** 1-based stage number the new attestation stage takes; later stages shift up by one. */
  position: number;
  /** Label of the attestation item, e.g. Attestation_PartAG9. */
  itemLabel: string;
}

export interface IStageShift { from: number; to: number; }
export interface IModuleStageChange { moduleId: string; from: number; to: number; }

export interface IAddAttestationStagePlan {
  ok: boolean;
  errors: string[];
  warnings: string[];
  position: number;
  itemLabel: string;
  newModuleId: string;
  newStageNumber: number;
  stageShifts: IStageShift[];
  moduleStageChanges: IModuleStageChange[];
  /** Set when the new stage becomes the first stage: the starting module moves to the attestation module. */
  startingModuleChange?: { from: string; to: string };
  /** panels that will receive the new module */
  panelsToUpdate: number;
  /** panels that already hold the new module id and are left alone */
  panelsSkipped: number;
}

type Panels = IPanelModulesConfig[] | undefined | null;

function stageNumbersOf(panelAssembly: IPanelAssemblyConfig): number[] {
  const set = new Set<number>();
  (panelAssembly?.allModules || []).forEach(m => { if (m?.stageNumber != null) { set.add(+m.stageNumber); } });
  return Array.from(set).sort((a, b) => a - b);
}

/** Next free numeric module id: above every module id, routing-rule key and routing target. */
export function nextMscatModuleId(panelAssembly: IPanelAssemblyConfig): number {
  let max = 0;
  const see = (id: any) => { const n = parseInt(id, 10); if (Number.isFinite(n)) { max = Math.max(max, n); } };
  (panelAssembly?.allModules || []).forEach(m => see(m?.id));
  Object.keys(panelAssembly?.routingRules || {}).forEach(key => {
    see(key);
    (panelAssembly.routingRules[key] || []).forEach(rule => see(rule?.module));
  });
  see(panelAssembly?.startingModule);
  return max + 1;
}

export function planAddAttestationStage(
  panelAssembly: IPanelAssemblyConfig | undefined | null,
  panels: Panels,
  input: IAddAttestationStageInput,
): IAddAttestationStagePlan {
  const errors: string[] = [];
  const warnings: string[] = [];
  const position = Math.floor(Number(input?.position));
  const itemLabel = (input?.itemLabel || '').trim();

  const empty: IAddAttestationStagePlan = {
    ok: false, errors, warnings, position, itemLabel,
    newModuleId: '', newStageNumber: position,
    stageShifts: [], moduleStageChanges: [], panelsToUpdate: 0, panelsSkipped: 0,
  };

  if (!panelAssembly || !Array.isArray(panelAssembly.allModules)) {
    errors.push('This framework has no panel assembly modules to add a stage to.');
    return empty;
  }

  const stages = stageNumbersOf(panelAssembly);
  const maxStage = stages.length ? stages[stages.length - 1] : 0;

  if (!itemLabel) {
    errors.push('Choose the attestation item label.');
  }
  if (!Number.isFinite(position) || position < 1 || position > maxStage + 1) {
    errors.push(`Position must be a stage number from 1 to ${maxStage + 1}.`);
  }

  const newModuleId = String(nextMscatModuleId(panelAssembly));
  if (panelAssembly.allModules.some(m => String(m.id) === newModuleId)) {
    errors.push(`Module id ${newModuleId} already exists.`);
  }

  const stageShifts: IStageShift[] = stages
    .filter(s => s >= position)
    .map(s => ({ from: s, to: s + 1 }));
  const moduleStageChanges: IModuleStageChange[] = panelAssembly.allModules
    .filter(m => m.stageNumber >= position)
    .map(m => ({ moduleId: String(m.id), from: m.stageNumber, to: m.stageNumber + 1 }));

  let panelsToUpdate = 0;
  let panelsSkipped = 0;
  (panels || []).forEach(panel => {
    if ((panel.modules || []).some(m => String(m.moduleId) === newModuleId)) { panelsSkipped++; }
    else { panelsToUpdate++; }
  });
  if (!(panels || []).length) {
    warnings.push('There are no panels yet; the module will be defined but no panel will hold the item.');
  }
  if (panelsSkipped) {
    warnings.push(`${panelsSkipped} panel(s) already hold module ${newModuleId} and are left unchanged.`);
  }

  const startingModuleChange = (stages.length && position <= stages[0])
    ? { from: String(panelAssembly.startingModule ?? ''), to: newModuleId }
    : undefined;

  const plan: IAddAttestationStagePlan = {
    ok: false, errors, warnings, position, itemLabel, newModuleId,
    newStageNumber: position, stageShifts, moduleStageChanges, startingModuleChange, panelsToUpdate, panelsSkipped,
  };

  // validate the structure the plan would produce, so Apply can never be enabled on a broken result
  // Problems the framework already had are warnings, not blockers: only a problem this change
  // would introduce stops Apply.
  if (!errors.length) {
    const before = new Set(validateMscatStageStructure(panelAssembly, panels));
    const after = cloneForValidation(panelAssembly, panels);
    applyPlanTo(after.panelAssembly, after.panels, plan);
    const afterIssues = validateMscatStageStructure(after.panelAssembly, after.panels);
    afterIssues.filter(e => !before.has(e)).forEach(e => errors.push(`After applying: ${e}`));
    before.forEach(e => warnings.push(`Existing issue (not caused by this change): ${e}`));
  }
  plan.ok = errors.length === 0;
  return plan;
}

function cloneForValidation(panelAssembly: IPanelAssemblyConfig, panels: Panels) {
  return {
    panelAssembly: <IPanelAssemblyConfig> JSON.parse(JSON.stringify(panelAssembly)),
    panels: <IPanelModulesConfig[]> JSON.parse(JSON.stringify(panels || [])),
  };
}

function applyPlanTo(panelAssembly: IPanelAssemblyConfig, panels: IPanelModulesConfig[], plan: IAddAttestationStagePlan) {
  const position = plan.position;

  // 1. shift later modules' stage numbers; ids and every other field stay as they are
  panelAssembly.allModules.forEach(m => { if (m.stageNumber >= position) { m.stageNumber = m.stageNumber + 1; } });

  // 2. re-key stage settings by the same shift (highest first so nothing is overwritten)
  if (!panelAssembly.stages) { panelAssembly.stages = []; }
  const stageSettings = panelAssembly.stages;
  Object.keys(stageSettings)
    .map(k => +k)
    .filter(k => Number.isFinite(k) && k >= position)
    .sort((a, b) => b - a)
    .forEach(k => { stageSettings[k + 1] = stageSettings[k]; delete stageSettings[k]; });
  stageSettings[position] = { isCalculatorEnabled: false, is_attestation: true };

  // 3. the attestation module
  panelAssembly.allModules.push({
    id: plan.newModuleId,
    stageNumber: position,
    item_count: 1,
    difficulty_range: [0, 0],
  });
  panelAssembly.numStages = stageNumbersOf(panelAssembly).length;
  if (plan.startingModuleChange) { panelAssembly.startingModule = plan.startingModuleChange.to; }

  // 4. every panel holds the module
  panels.forEach(panel => {
    if (!panel.modules) { panel.modules = []; }
    if (panel.modules.some(m => String(m.moduleId) === plan.newModuleId)) { return; }
    panel.modules.push({ moduleId: +plan.newModuleId, itemLabels: [plan.itemLabel] });
  });
}

/** Applies a plan in place. Refuses (returns false) when the plan is not ok. */
export function applyAddAttestationStagePlan(
  panelAssembly: IPanelAssemblyConfig,
  panels: Panels,
  plan: IAddAttestationStagePlan,
): boolean {
  if (!plan?.ok) { return false; }
  applyPlanTo(panelAssembly, <IPanelModulesConfig[]> (panels || []), plan);
  return true;
}

/**
 * Structural checks for an MSCAT panel assembly. Empty result = valid.
 *  - stage numbers are contiguous 1..N
 *  - every module's stage has a stage settings entry
 *  - routing rule keys and targets, and startingModule, name existing modules
 *  - every panel holds every module
 *  - an attestation stage holds exactly one module
 */
export function validateMscatStageStructure(
  panelAssembly: IPanelAssemblyConfig | undefined | null,
  panels: Panels,
): string[] {
  const errors: string[] = [];
  if (!panelAssembly?.allModules) { return ['No panel assembly modules.']; }
  const stages = stageNumbersOf(panelAssembly);
  if (stages.some((s, i) => s !== i + 1)) { return ['Stage numbers are not contiguous from 1.']; }

  const ids = new Set(panelAssembly.allModules.map(m => String(m.id)));
  stages.forEach(s => {
    if (!panelAssembly.stages?.[s]) { errors.push(`Stage ${s} has no stage settings entry.`); }
  });

  Object.keys(panelAssembly.routingRules || {}).forEach(key => {
    if (!ids.has(key)) { errors.push(`Routing rules are keyed by unknown module ${key}.`); }
    (panelAssembly.routingRules[key] || []).forEach(rule => {
      if (rule?.module != null && !ids.has(String(rule.module))) {
        errors.push(`Routing from module ${key} targets unknown module ${rule.module}.`);
      }
    });
  });
  const startId = panelAssembly.startingModule;
  if (startId != null && startId !== '') {
    const startModule = panelAssembly.allModules.find(m => String(m.id) === String(startId));
    if (!startModule) {
      errors.push(`Starting module ${startId} does not exist.`);
    } else if (stages.length && +startModule.stageNumber !== stages[0]) {
      errors.push(`Starting module ${startId} is in stage ${startModule.stageNumber}; it must be in stage ${stages[0]}.`);
    }
  }

  (panels || []).forEach(panel => {
    const held = new Set((panel.modules || []).map(m => String(m.moduleId)));
    ids.forEach(id => { if (!held.has(id)) { errors.push(`Panel ${panel.id} does not hold module ${id}.`); } });
  });

  stages.forEach(s => {
    if (!panelAssembly.stages?.[s]?.is_attestation) { return; }
    const inStage = panelAssembly.allModules.filter(m => m.stageNumber === s);
    if (inStage.length > 1) { errors.push(`Attestation stage ${s} holds ${inStage.length} modules; expected one.`); }
  });
  return errors;
}
